import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getAccountingActivity,
  getAccountingDocumentRequests,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

const statusClasses = {
  needed: "border-amber-200 bg-amber-50 text-amber-700",
  received: "border-emerald-200 bg-emerald-50 text-emerald-700",
  being_reviewed: "border-sky-200 bg-sky-50 text-sky-700",
  needs_new_copy: "border-rose-200 bg-rose-50 text-rose-700",
  done: "border-slate-200 bg-slate-100 text-slate-700",
  cancelled: "border-slate-200 bg-slate-100 text-slate-700",
};

function formatDate(value) {
  if (!value) return "";
  const [year, month, day] = String(value).split("-").map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function uploadPath(request) {
  const params = new URLSearchParams({
    document_type: request.document_type,
    request_id: String(request.id),
  });
  return `/salonbusinesscpa/documents?${params.toString()}`;
}

export default function SalonBusinessCpaRequests() {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const openRequests = useMemo(
    () => requests.filter((request) => !["done", "cancelled"].includes(request.status)),
    [requests]
  );

  const activityByRequest = useMemo(() => {
    return activity.reduce((next, event) => {
      if (!event.request_id) return next;
      const key = String(event.request_id);
      next[key] = next[key] || [];
      next[key].push(event);
      return next;
    }, {});
  }, [activity]);

  const loadRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const [data, activityData] = await Promise.all([
        getAccountingDocumentRequests(token),
        getAccountingActivity(token, { limit: 120 }).catch(() => null),
      ]);
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
      setActivity(Array.isArray(activityData?.events) ? activityData.events : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load document requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <SalonBusinessCpaShell
      title="Document requests"
      description="See records your accountant needs, why they matter, and what to upload next."
      actions={
        <button
          type="button"
          onClick={loadRequests}
          className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      }
    >
      {error ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              Open requests
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Upload these records first so review can keep moving.
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            {openRequests.length} open
          </span>
        </div>
      </section>

      <section className="space-y-3">
        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            Loading requests...
          </div>
        ) : requests.length ? (
          requests.map((request) => (
            <article
              key={request.id}
              className={`rounded-3xl border p-5 shadow-sm ${
                request.is_overdue
                  ? "border-amber-200 bg-amber-50"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-slate-950">
                      {request.title}
                    </h3>
                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                        statusClasses[request.status] ||
                        "border-slate-200 bg-slate-100 text-slate-700"
                      }`}
                    >
                      {request.status_label}
                    </span>
                    {request.is_overdue ? (
                      <span className="rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-semibold text-amber-700">
                        Please send when ready
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {request.client_note ||
                      "Please upload this record so your accounting review can continue."}
                  </p>
                  <p className="mt-2 text-xs font-semibold text-slate-500">
                    Upload type: {request.document_type_label}
                    {request.due_date ? ` / Requested by ${formatDate(request.due_date)}` : ""}
                  </p>
                </div>
                {!["done", "cancelled"].includes(request.status) ? (
                  <Link
                    to={uploadPath(request)}
                    className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full bg-rose-500 px-4 text-sm font-semibold text-white hover:bg-rose-600"
                  >
                    Upload this record
                  </Link>
                ) : null}
              </div>
              {activityByRequest[String(request.id)]?.length ? (
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Request history
                  </p>
                  <ol className="mt-3 space-y-3">
                    {activityByRequest[String(request.id)].slice(0, 5).map((event) => (
                      <li key={event.id} className="flex gap-3 text-sm">
                        <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-rose-500" />
                        <span>
                          <span className="font-semibold text-slate-800">
                            {event.title}
                          </span>
                          {event.body ? (
                            <span className="text-slate-600"> - {event.body}</span>
                          ) : null}
                          <span className="block text-xs text-slate-500">
                            {formatDateTime(event.occurred_at_utc)}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </article>
          ))
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
            No document requests yet.
          </div>
        )}
      </section>
    </SalonBusinessCpaShell>
  );
}
